/**
 * FolderTree: the whole workspace as one tree in the sidebar. The expanded
 * rows are flattened (`useFolderTreeRows`) and virtualized, so a very large
 * open folder keeps a bounded DOM. Rows open the item context menu and drag
 * and drop through the canvas's own builders (`itemContextMenuProps`,
 * `useItemDnd`), so a canvas item dropped on a tree folder moves there and a
 * tree row dropped on a canvas folder moves there too. The tree mounts its
 * own menu host (surface 'tree'), which never touches the canvas selection.
 * Rows also reorder among their siblings (`useTreeReorder`): a drag in a
 * row's top or bottom band draws an insertion line and places the item there.
 */

import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { defaultRangeExtractor, useVirtualizer, type Range } from '@tanstack/react-virtual';

import { ENTITY_ICONS } from '../../../theme/entityIcons';
import { useItemDnd } from '../../workspace/dnd/useItemDnd';
import { ItemContextMenuHost } from '../../workspace/item-menu/ItemContextMenuHost';
import { itemContextMenuProps } from '../../workspace/item-menu/useItemContextMenu';
import { TREE_ROW_HEIGHT_PX } from '../../workspace/layout';
import { FolderTreeRow } from './FolderTreeRow';
import { FolderTreeShowMoreRow } from './FolderTreeShowMoreRow';
import { TreeDropLine } from './TreeDropLine';
import type { ShowMoreEntry } from './useLevelPages';
import { useFolderTree } from './useFolderTree';
import { useTreeReorder } from './useTreeReorder';

export function FolderTree() {
  const tree = useFolderTree();
  const { rows, entries, items, activeIndex, focusPending } = tree;
  // The virtual list is `entries` (rows plus "Show more" rows); the roving
  // tab stop and the keyboard work in `rows`. Map a row to its entry.
  const entryIndexOfRow = useMemo(
    () => entries.flatMap((entry, index) => (entry.kind === 'row' ? [index] : [])),
    [entries],
  );
  const activeEntry = entryIndexOfRow[activeIndex] ?? 0;
  const dndProps = useItemDnd(items, tree.currentId);
  const reorder = useTreeReorder(rows);
  const scrollRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => TREE_ROW_HEIGHT_PX,
    overscan: 8,
    // The tab stop stays mounted however far the list scrolls, so Tab always
    // lands back in the tree and focus never falls to the document.
    rangeExtractor: useCallback(
      (range: Range) => {
        const indexes = defaultRangeExtractor(range);
        return indexes.includes(activeEntry)
          ? indexes
          : [...indexes, activeEntry].sort((a, b) => a - b);
      },
      [activeEntry],
    ),
  });
  const virtualRows = virtualizer.getVirtualItems();

  // The insertion line sits on the hovered row's top or bottom edge.
  const { dropLine } = reorder;
  const dropLineRow = dropLine
    ? virtualRows.find(
        (v) => entryIndexOfRow[rows.findIndex((r) => r.item.id === dropLine.rowId)] === v.index,
      )
    : undefined;

  // After a keyboard move: bring the row into view, then move DOM focus.
  useEffect(() => {
    if (!focusPending.current) return;
    virtualizer.scrollToIndex(activeEntry);
    const row = scrollRef.current?.querySelector<HTMLElement>(`[data-row-index="${activeIndex}"]`);
    if (!row) return;
    focusPending.current = false;
    row.focus({ preventScroll: true });
  }, [activeIndex, activeEntry, virtualRows, virtualizer, focusPending]);

  // After "Show more": move the tab stop (and focus, if the button had it) to
  // the first row it loaded, so focus never falls to <body> when the last
  // page unmounts the button.
  const { showMore, moveTo } = tree;
  const onShowMore = useCallback(
    async (entry: ShowMoreEntry, button: HTMLElement) => {
      const firstNewId = await showMore(entry);
      if (firstNewId && document.activeElement === button) moveTo(firstNewId);
    },
    [showMore, moveTo],
  );

  return (
    <Box display="flex" flexDirection="column" flex="1" minH={0}>
      <Text
        id={labelId}
        px={4}
        pt={3}
        pb={1}
        fontSize="12px"
        fontWeight="medium"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color="text.muted"
      >
        {ENTITY_ICONS.project.pluralName}
      </Text>
      <Box ref={scrollRef} flex="1" minH={0} overflowY="auto" px={2} pb={2}>
        <Box
          role="tree"
          aria-labelledby={labelId}
          position="relative"
          h={`${virtualizer.getTotalSize()}px`}
          onKeyDown={(event) => {
            reorder.onKeyDown(event, rows[activeIndex]);
            tree.onKeyDown(event);
          }}
        >
          {virtualRows.map((virtualRow) => {
            const entry = entries[virtualRow.index];
            const style = {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            } as const;
            if (entry?.kind === 'more') {
              return (
                <FolderTreeShowMoreRow
                  key={`more:${entry.levelKey}`}
                  level={entry.level}
                  style={style}
                  isLoading={tree.loadingMore.has(entry.levelKey)}
                  onShowMore={(button) => void onShowMore(entry, button)}
                />
              );
            }
            const row = entry ? rows[entry.rowIndex] : undefined;
            if (!entry || !row) return null;
            return (
              <FolderTreeRow
                key={row.item.id}
                row={row}
                index={entry.rowIndex}
                style={style}
                isActive={entry.rowIndex === activeIndex}
                isCurrent={row.item.id === tree.currentId}
                menuProps={itemContextMenuProps(row.item, 'tree')}
                dndProps={reorder.rowProps(row, dndProps(row.item))}
                onOpen={tree.open}
                onToggle={tree.toggle}
                onFocusRow={tree.setActiveId}
              />
            );
          })}
          {dropLine && dropLineRow && (
            <TreeDropLine
              top={dropLine.position === 'before' ? dropLineRow.start : dropLineRow.end}
              level={dropLine.level}
              position={dropLine.position}
            />
          )}
        </Box>
      </Box>
      <ItemContextMenuHost items={items} surface="tree" />
    </Box>
  );
}
